import { i18n } from '@marine/i18n';
import {
  ORG_ENTITLEMENT,
  ORG_LIMIT,
  type OrgLimit,
  type OrgPlan,
  PLAN_SLUG,
  PLANS_IN_ORDER,
  type PlanSlug,
} from './orgPlan';
import { type PlanCatalog, planPriceShort } from './serverPlan';

/**
 * What the limit-reached modal (U02, MDC-4520) says, worked out once for both apps.
 *
 * Plain functions over the plan catalog (`usePlanCatalog`, the backend's `GET /plans`), so the
 * words and the comparison are tested without rendering anything, the dashboard's modal and
 * mobile's sheet cannot say different things about the same limit, and the price on the upgrade
 * button is the one checkout charges.
 */

/**
 * The cheapest plan above `current` that raises `resource`.
 *
 * Not simply the next plan up: Essentials covers one boat, the same as Free, so a Free organization
 * out of boats is offered Plus. `null` when no plan goes further, which is where Enterprise begins.
 */
export const nextPlanFor = (current: PlanSlug, resource: OrgLimit, catalog: PlanCatalog): PlanSlug | null => {
  const currentLimit = catalog[current].limits[resource];

  if (currentLimit === null) {
    return null;
  }

  const above = PLANS_IN_ORDER.slice(PLANS_IN_ORDER.indexOf(current) + 1);

  return (
    above.find((slug) => {
      const limit = catalog[slug].limits[resource];

      return limit === null || limit > currentLimit;
    }) ?? null
  );
};

/** One row of the comparison: what the organization has now, and what the offered plan gives. */
export type LimitComparisonRow = {
  key: OrgLimit | 'customLocations';
  label: string;
  current: string;
  /** `true` draws a check, for a feature the offered plan includes. */
  next: string | true;
  /** The row that stopped the user, which both apps highlight. */
  isLimitHit: boolean;
};

const EMPTY = '—';

const count = (limit: number | null): string =>
  limit === null ? i18n.t('subscription:limits.unlimited') : String(limit);
const storage = (limit: number | null): string =>
  limit === null ? i18n.t('subscription:limits.unlimited') : i18n.t('subscription:limits.storageAmount', { limit });

/** The rows the design compares, in its order. */
export const limitComparison = (
  current: PlanSlug,
  next: PlanSlug,
  resource: OrgLimit,
  catalog: PlanCatalog,
): LimitComparisonRow[] => {
  const now = catalog[current];
  const then = catalog[next];

  const limitRow = (key: OrgLimit, label: string, format: (limit: number | null) => string): LimitComparisonRow => ({
    key,
    label,
    current: format(now.limits[key]),
    next: format(then.limits[key]),
    isLimitHit: key === resource,
  });

  const rows = [
    limitRow(ORG_LIMIT.boats, i18n.t('subscription:limits.rows.boats'), count),
    limitRow(ORG_LIMIT.users, i18n.t('subscription:limits.rows.users'), count),
    limitRow(ORG_LIMIT.storageGb, i18n.t('subscription:limits.rows.storage'), storage),
  ];

  const hasLocations = (entitlements: ReadonlySet<string>) => entitlements.has(ORG_ENTITLEMENT.customLocations);

  rows.push({
    key: 'customLocations',
    label: i18n.t('subscription:limits.rows.customLocations'),
    current: hasLocations(now.entitlements) ? i18n.t('subscription:limits.yes') : EMPTY,
    next: hasLocations(then.entitlements) ? true : EMPTY,
    isLimitHit: false,
  });

  return rows;
};

/**
 * "Your Essentials plan covers one boat." — the whole sentence, so a language can order it its own
 * way. Counts go through `count` for the plural forms; an unlimited plan has a sentence of its own.
 */
const coversSentence = (resource: OrgLimit, limit: number | null, plan: string): string => {
  switch (resource) {
    case ORG_LIMIT.boats:
      return limit === null
        ? i18n.t('subscription:limits.covers.boatsUnlimited', { plan })
        : i18n.t('subscription:limits.covers.boats', { plan, count: limit });
    case ORG_LIMIT.users:
      return limit === null
        ? i18n.t('subscription:limits.covers.usersUnlimited', { plan })
        : i18n.t('subscription:limits.covers.users', { plan, count: limit });
    case ORG_LIMIT.storageGb:
      return i18n.t('subscription:limits.covers.storage', { plan, storage: storage(limit) });
    case ORG_LIMIT.aiQueriesPerMonth:
      return i18n.t('subscription:limits.covers.aiQueries', { plan, amount: count(limit) });
    case ORG_LIMIT.theBoatDbCompares:
      return i18n.t('subscription:limits.covers.compares', { plan, amount: count(limit) });
  }
};

/** One line on what the offered plan is for, from the MDC-4517 table's "For" row. */
const planPitch = (slug: PlanSlug, catalog: PlanCatalog): string => {
  const { name, limits } = catalog[slug];

  switch (slug) {
    case PLAN_SLUG.free:
      return '';
    case PLAN_SLUG.essentials:
      return i18n.t('subscription:limits.pitch.essentials', { name, storage: storage(limits.storageGb) });
    case PLAN_SLUG.plus:
      return i18n.t('subscription:limits.pitch.plus', { name, users: count(limits.users).toLowerCase() });
    case PLAN_SLUG.pro:
      return i18n.t('subscription:limits.pitch.pro', {
        name,
        boats: count(limits.boats).toLowerCase(),
        users: count(limits.users).toLowerCase(),
      });
  }
};

/** The heading, which names what the offered plan gives for the limit that was hit. */
const upgradeTitle = (
  resource: OrgLimit,
  currentLimit: number | null,
  next: PlanSlug,
  catalog: PlanCatalog,
): string => {
  const { name } = catalog[next];
  const nextLimit = catalog[next].limits[resource];

  switch (resource) {
    case ORG_LIMIT.boats:
      return currentLimit === 1
        ? i18n.t('subscription:limits.upgradeTitle.secondBoat', { name })
        : i18n.t('subscription:limits.upgradeTitle.boats', { name, amount: count(nextLimit) });
    case ORG_LIMIT.users:
      return i18n.t('subscription:limits.upgradeTitle.users', { name, amount: count(nextLimit) });
    case ORG_LIMIT.storageGb:
      return i18n.t('subscription:limits.upgradeTitle.storage', { name, storage: storage(nextLimit) });
    case ORG_LIMIT.aiQueriesPerMonth:
      return i18n.t('subscription:limits.upgradeTitle.aiQueries', { name, amount: count(nextLimit) });
    case ORG_LIMIT.theBoatDbCompares:
      return i18n.t('subscription:limits.upgradeTitle.compares', { name });
  }
};

export type LimitReachedNotice = {
  title: string;
  description: string;
};

/**
 * What mobile says when `resource` has run out (MDC-4606): the limit, and nothing about another plan.
 *
 * Plans are bought on the web through Stripe, and the App Store and Google Play forbid pointing
 * users at a purchase outside the app. So no plan is offered, no price is shown and no one is told
 * to upgrade, until mobile sells plans in-app (RevenueCat).
 */
export const limitReachedNotice = (plan: OrgPlan, resource: OrgLimit, catalog: PlanCatalog): LimitReachedNotice => {
  const currentName = catalog[plan.slug ?? PLAN_SLUG.pro].name;

  return {
    title: i18n.t('subscription:limits.reachedTitle'),
    description: coversSentence(resource, plan.limits[resource], currentName),
  };
};

export type LimitReachedContent = {
  title: string;
  description: string;
  current: { slug: PlanSlug; label: string };
  /** `null` when no plan goes further: the modal then has no comparison and no upgrade. */
  next: { slug: PlanSlug; label: string; upgradeLabel: string } | null;
  rows: LimitComparisonRow[];
  /** Under the table. Only for someone who can upgrade: it is a promise about what upgrading does. */
  reassurance: string | null;
};

/**
 * Everything the modal says when `resource` has run out.
 *
 * `canUpgrade` is the owner's `org:sys_billing:manage`. Anyone else is told who can upgrade instead
 * of being offered a button that the billing routes would refuse. That wording is still to be
 * confirmed with design (MDC-4517, "what a member sees at a limit").
 */
export const limitReachedContent = (
  plan: OrgPlan,
  resource: OrgLimit,
  canUpgrade: boolean,
  catalog: PlanCatalog,
): LimitReachedContent => {
  // A plan the app doesn't know is read as the top plan, so it is at least never offered an
  // upgrade to something smaller.
  const currentSlug = plan.slug ?? PLAN_SLUG.pro;
  const currentName = catalog[currentSlug].name;
  const currentLimit = plan.limits[resource];
  const nextSlug = nextPlanFor(currentSlug, resource, catalog);
  const covers = coversSentence(resource, currentLimit, currentName);
  const askOwner = i18n.t('subscription:limits.askOwner');

  const current = { slug: currentSlug, label: i18n.t('subscription:limits.currentLabel', { name: currentName }) };

  if (!nextSlug) {
    return {
      title: i18n.t('subscription:limits.reachedTitle'),
      description: `${covers} ${i18n.t('subscription:limits.noBiggerPlan')}`,
      current,
      next: null,
      rows: [],
      reassurance: null,
    };
  }

  const nextName = catalog[nextSlug].name;
  const price = planPriceShort(catalog[nextSlug].price);

  return {
    title: upgradeTitle(resource, currentLimit, nextSlug, catalog),
    description: `${covers} ${planPitch(nextSlug, catalog)}${canUpgrade ? '' : ` ${askOwner}`}`,
    current,
    next: {
      slug: nextSlug,
      label: `${nextName} · ${price}`,
      upgradeLabel: i18n.t('subscription:limits.upgradeLabel', { name: nextName, price }),
    },
    rows: limitComparison(currentSlug, nextSlug, resource, catalog),
    reassurance: canUpgrade ? i18n.t('subscription:limits.reassurance') : null,
  };
};
